import AppContext from "PFApp/app_context";
import { DetailsPanel, useDetailsPanelContext } from "PFApp/booking/components/details_panel";
import { useBookingModuleContext } from "PFApp/booking/parts/providers/booking_module_context_provider";
import { useEdgeToEdgeLayout } from "PFApp/react_app_layout";
import PageTabs from "PFComponents/page/page_tabs";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { PermissionRule } from "PFTypes";
import React, { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

import css from "./booking_page.module.scss";
import { BookingFormContextProvider } from "./components/booking_form";
import { DeleteBookingModalContextProvider } from "./components/delete_booking_modal/delete_booking_modal_context_provider";
import ApprovalsTab from "./parts/approvals/approvals_tab";
import ApprovalsControls from "./parts/approvals/controls";
import OverviewTab from "./parts/overview/overview_tab";
import { useSyncRows } from "./parts/overview/use_sync_rows";
import { ReportsTab } from "./parts/reports/reports_tab";
import { useQueryParamsTriggers } from "./use_query_params_triggers";

export const BOOKING_PAGE_HEADER_HEIGHT = 72;

const BookingPage = () => {
  useEdgeToEdgeLayout();
  useQueryParamsTriggers();
  const {
    store: {
      pages: {
        booking: { tab, changeTab, initialTabData }
      }
    }
  } = useContext(AppContext);
  const { data: currentProfile } = useCurrentProfile();
  const { detailsData } = useDetailsPanelContext();
  const { headerDisabled } = useBookingModuleContext();

  const { t } = useTranslation("bookingModule");
  const syncRows = useSyncRows();
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const headerRef = useRef(null);

  const showApprovals = isPermittedTo(PermissionRule.BookingApprover);
  const isBookingResourcer = isPermittedTo(PermissionRule.BookingResourcer);
  const showReports = localStorage.getItem("bm_reports_enabled") === "true" && isBookingResourcer; // TODO: [ENG-1529] temporary flag to hide the feature. Remove once Export feature is ready.
  const tabs = [
    { id: "overview", displayElement: t("tabs.overview"), disabled: headerDisabled },
    { id: "approvals", displayElement: t("tabs.approvals"), disabled: !showApprovals || headerDisabled }
  ];

  if (showReports) {
    tabs.push({ id: "reports", displayElement: t("tabs.reports"), disabled: !showReports || headerDisabled });
  }

  const TabComponent =
    {
      overview: OverviewTab,
      approvals: showApprovals && ApprovalsTab,
      reports: showReports && ReportsTab
    }[tab] || OverviewTab;

  const handleDelete = async (entity) => {
    if (detailsData.type !== "booking" || !entity) {
      return;
    }
    const parentActivityIds = entity.activity?.parent_activity?.id
      ? [entity.activity.parent_activity.id]
      : [];
    await syncRows({ profileId: entity.profile_id, activityId: entity.activity?.id, parentActivityIds });
  };

  return (
    <div>
      <div ref={headerRef} className={css.header} style={{ height: BOOKING_PAGE_HEADER_HEIGHT }}>
        <PageTabs
          rootClassName={css.tabs}
          items={tabs}
          active={tab}
          handleClick={(value) => changeTab(value.id)}
        />
        {tab === "approvals" && <ApprovalsControls />}
      </div>
      <BookingFormContextProvider onModalSubmit={syncRows}>
        <DeleteBookingModalContextProvider>
          <TabComponent headerRef={headerRef} initialData={initialTabData} />
          <DetailsPanel data={detailsData} onDeleted={handleDelete} />
        </DeleteBookingModalContextProvider>
      </BookingFormContextProvider>
    </div>
  );
};

export default BookingPage;
